import { ChangeDetectionStrategy, Component, computed, inject, input, OnChanges } from '@angular/core';
import { BarInfo, BarInfoPct, buildPctStack } from '../../../../services/stacked-bars';
import { VBarOptions, VbarStyle } from '../vbar-batch/vbar-batch.component';
import { UrlService } from '../../../../services/url.service';
import { Attributes, Selection } from '../../base-dashboard/base-dashboard-config';
import { isNil, range, reverse, zipWith } from 'lodash-es';
import { Axis } from '../../../../services/axis';
import { getFormattedValue } from '../../../components/table/table/table.model';
import { Path } from '../../../../services/data-tree';
import { TooltipDirective, TooltipElement } from '@cumlaude/shared-components-overlays';
import { isDefined } from '@cumlaude/shared-utils';

export type VMeasure = { type: 'string' | 'number' | 'percentage'; value: any; format?: string; visible?: boolean };

export const NO_MEASURE: VMeasure = { type: 'string', value: undefined };

export type VPartitionData = {
	stacks: BarInfo[][];
	measure: VMeasure;
	label: string;
	className?: string;
	/** Hoogte van deze partition op de schaal van yAxis */
	qty: number | null;
	selection?: Selection;
	areas?: { className: string; qty?: number }[]; // qty undefined mag alleen op het eind, en vertaalt dan naar max
	tooltip?: TooltipElement[];
	path: Path<Attributes, number[]>;
};

let nextId = 0;

@Component({
	selector: 'app-vbar-series',
	templateUrl: './vbar-series.component.html',
	styleUrls: ['./vbar-series.component.scss'],
	imports: [TooltipDirective],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VbarSeriesComponent implements OnChanges {
	protected readonly urlService = inject(UrlService);

	partitions = input<VPartitionData[]>([]);

	yAxis = input<Axis>({ min: 0, max: 100, ticks: [] });

	/** Hoogte van de grafiek in pixels */
	barHeight = input(100);

	barWidth = input<number | undefined>(undefined);

	style = input(VbarStyle.BAR);

	options = input<VBarOptions>();

	barSpace = computed(() => this.barWidth() ?? (this.style() === VbarStyle.PLANE ? 36 : 28));

	rectWidth = computed(() => (this.style() === VbarStyle.PLANE ? (this.barSpace() - 12) / this.barSpace() : 0.6));

	rectPadding = computed(() => (this.style() === VbarStyle.PLANE ? 6 / this.barSpace() : 0.2));

	rectWidthPx = computed(() => this.rectWidth() * this.barSpace());

	rectPaddingPx = computed(() => this.rectPadding() * this.barSpace());

	paddingTop = input(18);

	paddingBottom = input(40);

	tooltipClass = input<string | undefined>(undefined);

	/** Als de bar minder pixels is verdwijnt de tekst */
	textLineHeight = 20;

	/** paddingLeft: marge links van de grafiek in px, hierin vallen de labels van de y-as. Indien niet gespecificeerd wordt dit berekend op basis van de labels van de y-as. */
	paddingLeftInput = input<number | undefined>(undefined, { alias: 'paddingLeft' });

	paddingLeft = computed(() => {
		const paddingLeft = this.paddingLeftInput();
		if (!isNil(paddingLeft)) return paddingLeft;

		return this.yAxis().ticks.length === 0 ? 0 : Math.max(10, ...this.yAxis().ticks.map(({ label }) => label.length * 9));
	});

	gutterPx = computed(() => (this.style() === VbarStyle.PLANE ? 2 : 3));

	/** Geeft aan hoe de text in de bars is gealigned */
	textAlign = input<'top' | 'middle' | 'bottom'>('middle');

	get xrangeIncl() {
		return range(0, this.partitions().length + 1);
	}

	xlabels!: { label: string; className?: string }[];

	/**
	 * Elke partition uit de input wordt omgezet in één pctStack: de verschillende stacks worden samengevoegd, met nieuwe bars voor gutters ertussenin.
	 */
	pctStacks!: BarInfoPct[][];

	measures!: string[];

	y0px = 100;

	/**
	 * zie getClipPathId()
	 */
	componentId = nextId++;

	/**
	 * Controleert of dit een PLANE stijl is met exact één bar in de partition
	 */
	private isSingleBarPlane(i: number): boolean {
		return this.style() === VbarStyle.PLANE && this.partitions()[i].stacks.flat().length === 1;
	}

	/**
	 * In PLANE style met een partition van exact één bar: bepaalt of de tekst niet in de bar past en daarom buiten de partition getoond moet worden.
	 */
	shouldShowTextOutsidePartition(i: number, pct: number): boolean {
		return this.isSingleBarPlane(i) && this.barPxHeight(pct, i) < this.textLineHeight;
	}

	ngOnChanges() {
		this.pctStacks = [];
		this.measures = [];
		this.xlabels = [];
		this.partitions().forEach((partitionData) => {
			const totalPx = this.qtyToPx(partitionData.qty);
			const stacks = totalPx >= 0 ? partitionData.stacks : reverse(partitionData.stacks).map((bars) => reverse(bars));
			this.pctStacks.push(buildPctStack(stacks, totalPx, this.gutterPx()));
			this.measures.push(
				(partitionData.measure.visible ?? true)
					? getFormattedValue(partitionData.measure.value, partitionData.measure.type, partitionData.measure.format)
					: ''
			);
			this.xlabels.push({ label: partitionData.label, className: partitionData.className });
		});
		this.y0px = this.qtyToPx(this.yAxis().max);
	}

	getHeight() {
		return this.barHeight() + this.paddingBottom() + this.paddingTop();
	}

	getWidth() {
		return this.partitions().length * this.barSpace() + this.paddingLeft() + 0.5;
	}

	getViewBox() {
		return `0 0 ${this.getWidth()} ${this.getHeight()}`;
	}

	/**
	 * Coördinaten van de balkjes worden aangegeven t.o.v. de partition waar ze in zitten.
	 * De oorsprong ligt op de y=0 as-lijn, met y in pixels omhoog = negatief in SVG.
	 */
	getPartitionTransform(i: number) {
		return `translate(${this.paddingLeft() + i * this.barSpace()} ${this.y0px + this.paddingTop()})`;
	}

	/**
	 * Pixel-hoogte van de partition (met teken: positief = omhoog, negatief = omlaag).
	 * Voor PLANE stijl met één bar wordt minimaal 3 pixels aangehouden.
	 */
	getPartitionPx(i: number): number {
		let px = this.qtyToPx(this.partitions()[i].qty);
		if (this.isSingleBarPlane(i) && Math.abs(px) < 3) {
			px = px >= 0 ? 3 : -3;
		}
		return px;
	}

	/**
	 * SVG y-attribuut (bovenkant van de rect) voor een bar-segment, in pixel-coördinaten van de partition.
	 * Positieve hoeveelheden liggen boven de x-as, wat overeenkomt met negatieve SVG-y.
	 * Math.min zorgt ervoor dat we altijd de bovenkant teruggeven, ongeacht het teken van pct.
	 */
	barY(pctStart: number, pct: number, i: number): number {
		return Math.min(-this.pctToPx(i, pctStart), -this.pctToPx(i, pctStart + pct));
	}

	/**
	 * Pixel-hoogte van een bar-segment.
	 * Math.abs zorgt voor een positieve waarde, ongeacht het teken van pct.
	 */
	barPxHeight(pct: number, i: number): number {
		return Math.abs(this.pctToPx(i, pct));
	}

	/**
	 * SVG y-attribuut voor de clip-rect die de volledige partition omsluit.
	 * Dit is het speciale geval van barY over het volledige bereik (0% → 100%).
	 */
	partitionClipY(i: number): number {
		return this.barY(0, 100, i);
	}

	/**
	 * Hoogte in pixels van de clip-rect die de volledige partition omsluit.
	 * Dit is het speciale geval van barPxHeight over het volledige bereik (100%).
	 */
	partitionClipHeight(i: number): number {
		return this.barPxHeight(100, i);
	}

	getPartitionRectangles() {
		return this.partitions()
			.map((p, i) => {
				const px = this.qtyToPx(p.qty);
				return {
					x: this.paddingLeft() + i * this.barSpace() + this.rectPaddingPx(),
					y: this.y0px + this.paddingTop() - Math.max(px, 0),
					width: this.rectWidthPx(),
					height: Math.abs(px),
				};
			})
			.filter(({ height }) => height);
	}

	getBarTransform(i: number) {
		return `translate(${this.paddingLeft() + i * this.barSpace()} ${this.y0px + this.paddingTop()})`;
	}

	/**
	 * Geeft de transformatie voor de tekst in een bar, in pixel-coördinaten van de partition.
	 * Alleen translatie (en evt. rotatie voor BAR-stijl); geen schaling meer.
	 */
	getTextTransform(i: number, pctStart: number, pct: number) {
		const x = 0.5 * this.barSpace();
		let y = 0;

		switch (this.textAlign()) {
			case 'top':
				y = -this.pctToPx(i, pctStart + pct) + 12;
				break;
			case 'middle':
				y = -this.pctToPx(i, pctStart + 0.5 * pct);
				break;
			case 'bottom':
				y = -this.pctToPx(i, pctStart) - 12;
				break;
		}

		return `translate(${x} ${y})${this.style() === VbarStyle.PLANE ? '' : ' rotate(270)'}`;
	}

	/**
	 * Berekent de transformatie voor tekst buiten de partition (voor PLANE stijl met één bar die te klein is voor de tekst).
	 * Deze tekst wordt boven de bar geplaatst, buiten de clipPath van de partition.
	 */
	getTextTransformOutsidePartition(i: number) {
		const qty = this.partitions()[i].qty;

		// Plaats tekst boven de bar, buiten de clipPath van de partition
		// x en y wijzen naar het midden van de tekst
		const x = this.paddingLeft() + (i + 0.5) * this.barSpace();
		const y = this.paddingTop() + this.y0px - Math.max(3, this.qtyToPx(qty)) - this.textLineHeight / 2;

		return `translate(${x} ${y})`;
	}

	getXAxisTransform() {
		if (this.style() == VbarStyle.PLANE) {
			// Align x-as labels aan de onderkant van de paddingBottom
			return `translate(${this.paddingLeft()} ${this.barHeight() + this.paddingTop() + this.paddingBottom() - this.textLineHeight})`;
		}
		// Align x-as en labels aan de onderkant van de data
		return `translate(${this.paddingLeft()} ${this.barHeight() + this.paddingTop()})`; // + this.paddingBottom() - this.textLineHeight})`;
	}

	/**
	 * Stelsel met de oorsprong bij de logische oorsprong van de data.
	 */
	getYAxisTransform() {
		return `translate(${this.paddingLeft()} ${this.y0px + this.paddingTop()})`;
	}

	/**
	 * Stelsel met y=0 een stukje boven de partitions en positieve y omlaag.
	 */
	getMeasureTransform(i: number) {
		const { qty } = this.partitions()[i];
		const offsetPx = (qty ?? 0) >= 0 ? -8 : 8;
		return `translate(${this.paddingLeft() + (i + 0.5) * this.barSpace()} ${this.paddingTop() + this.y0px + offsetPx - this.qtyToPx(qty)})`;
	}

	/**
	 * Een uniek id zodat voor elke bar een <clipPath> te definiëren is waarnaar later terug gerefereerd kan worden.
	 * Omdat het uniek moet zijn over de hele web page, krijgt ook elk vbar-series component een uniek (oplopend) id.
	 * Het zou handiger zijn als hier een functie in Angular voor was. https://github.com/angular/angular/issues/5145
	 */
	getClipPathId(i: number, j: number) {
		return `cp-${this.componentId}-${i}-${j}`;
	}

	getPartitionClipPathId(i: number) {
		return `pcp-${this.componentId}-${i}`;
	}

	onBarClick(record: BarInfo) {
		if (isDefined(record.clickHandler)) record.clickHandler();
		else this.urlService.navigateToSelection(record.selection);
	}

	onTextClick(i: number) {
		this.urlService.navigateToSelection(this.partitions()[i].selection);
	}

	getSVGAreas(i: number): { y: number; height: number; className: string }[] {
		const areas = this.partitions()[i].areas ?? this.yAxis().areas;
		if (!areas) return [];

		const { min, max } = this.yAxis();
		const qties = areas.map(({ qty }) => qty ?? max);
		const classes = areas.map(({ className }) => className);
		return zipWith([min, ...qties.slice(0, -1)], [...qties], classes, (lo, hi, className) => ({
			y: -this.qtyToPx(hi),
			height: this.qtyToPx(hi - lo),
			className,
		}));
	}

	getSVGTicks() {
		return this.yAxis().ticks.map(({ qty, label }) => ({
			y: -this.qtyToPx(qty),
			label,
		}));
	}

	/**
	 * Berekent de x-positie van het Trend-icoontje voor een 'voorlopig' xlabel, zodat de tekst en het icoontje
	 * samen gecentreerd staan op x=0.
	 * De tekst staat met text-anchor="middle" op x=-7 (= -(iconWidth + gap) / 2).
	 * Het icoontje begint op x = textWidth/2 - (iconWidth - gap) / 2.
	 * Schatting tekstbreedte: 7.5px per karakter (12px font, weight 600).
	 */
	getVoorlopigIconX(label: string): number {
		const textWidth = label.length * 7.5;
		const gap = 4; // 0.25rem
		const iconWidth = 10;
		return textWidth / 2 - (iconWidth - gap) / 2;
	}

	qtyToPx(qty: number | null) {
		if (qty === null) return 0;

		const { min, max } = this.yAxis();
		return (this.barHeight() * qty) / (max - min);
	}

	pctToPx(i: number, pct: number): number {
		const totalPx = this.getPartitionPx(i);
		return (pct * totalPx) / 100;
	}

	VbarStyle = VbarStyle;
}
